<template>
  <div v-if="value"
    class="cell is-col-span-1 is-flex"
    style="position: relative"
    :inert="isSaving"
    @focusout="onComponentFocusOut">
    <div v-if="isSaving"
      class="quote-item-saving-overlay" />
    <article class="box is-flex-grow-1">
      <div class="columns mb-0">
        <div class="column is-flex is-align-items-center"
          style="position: relative;">
          <span v-show="isNewCustomerPrompt"
            class="icon is-synced-icon"
            :class="[ 'has-text-warning' ]">
            <i class="mdi mdi-circle-medium" />
          </span>
          <i class="icon mdi mdi-account-outline mdi-24px" />
          <p class="title pl-1">Customer
            <span v-if="innerCustomer.isNew"
              class="has-badge-rounded has-badge-small has-badge-info"
              :data-badge="'New'" />
          </p>
        </div>
        <div class="column is-flex is-justify-content-flex-end">
          <div>
            <div class="pretty p-default p-round p-smooth is-size-7">
              <input id="chk-pretty-customer-type"
                v-model="innerCustomer.isPerson"
                name="chk-pretty-customer-type"
                :value="false"
                type="radio">
              <div class="state p-primary">
                <label class="is-size-7">Company</label>
              </div>
            </div>
            <div class="pretty p-default p-round p-smooth is-size-7">
              <input id="chk-pretty-customer-type"
                v-model="innerCustomer.isPerson"
                name="chk-pretty-customer-type"
                :value="true"
                type="radio">
              <div class="state p-primary">
                <label class="is-size-7">Person</label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="is-divider" />
      <div class="field">
        <div v-if="!innerCustomer.isPerson"
          class="field">
          <label class="label is-required-label">Company Name</label>
          <div class="control">
            <bulma-autocomplete v-model="innerCustomer.companyName"
              :data="filteredCustomers"
              field="companyName"
              @input="searchCustomer('companyName')"
              @select="selectCustomer"
              :loading="isSearchingCustomer"
              :expanded="true"
              :open-on-focus="innerCustomer.isNew"
              :class-prop="!$v.customer.companyName.$error ? 'is-required-field' : 'is-invalid'"
              placeholder="Company Name">
              <template slot-scope="props">
                <div>{{ props.option.value }}</div>
                <div class="help is-primary">
                  <span class="pr-1">
                    <i class="mdi mdi-phone mdi-12px" />
                  </span>
                  <span>{{ props.option.phoneNo }}</span>
                </div>
              </template>
            </bulma-autocomplete>
            <span v-if="$v.customer.companyName.$error && !$v.customer.companyName.ormMaxLength"
              class="help is-danger">{{ ormMessages.companyName.ormMaxLength }}</span>
            <span v-else-if="$v.customer.companyName.$error && (!$v.customer.companyName.required || !$v.customer.companyName.isNotWhitespaceOnly)"
              class="help is-danger">Company Name is required</span>
          </div>
        </div>
        <div v-else
          class="field-body">
          <div class="field">
            <label class="label is-required-label">First Name</label>
            <div class="control">
              <bulma-autocomplete v-model="innerCustomer.firstname"
                :data="filteredCustomers"
                field="firstname"
                @input="searchCustomer('firstname')"
                @select="selectCustomer"
                :loading="isSearchingCustomer"
                :expanded="true"
                :open-on-focus="innerCustomer.isNew"
                :maxlength="120"
                :class-prop="!$v.customer.firstname.$error ? 'is-required-field' : 'is-invalid'"
                placeholder="First Name">
                <template slot-scope="props">
                  <div>{{ props.option.value }}</div>
                  <div class="help is-primary">
                    <span class="pr-1">
                      <i class="mdi mdi-phone mdi-12px" />
                    </span>
                    <span>{{ props.option.phoneNo }}</span>
                  </div>
                </template>
              </bulma-autocomplete>
            </div>
          </div>
          <div class="field">
            <label class="label is-required-label">Last Name</label>
            <div class="control">
              <bulma-autocomplete v-model="innerCustomer.lastname"
                :data="filteredCustomers"
                field="lastname"
                @input="searchCustomer('lastname')"
                @select="selectCustomer"
                :loading="isSearchingCustomer"
                :expanded="true"
                :open-on-focus="innerCustomer.isNew"
                :maxlength="120"
                :class-prop="!$v.customer.lastname.$error ? 'is-required-field' : 'is-invalid'"
                placeholder="Last Name">
                <template slot-scope="props">
                  <div>{{ props.option.value }}</div>
                  <div class="help is-primary">
                    <span class="pr-1">
                      <i class="mdi mdi-phone mdi-12px" />
                    </span>
                    <span>{{ props.option.phoneNo }}</span>
                  </div>
                </template>
              </bulma-autocomplete>
            </div>
          </div>
        </div>
      </div>
      <transition name="custom-classes-transition"
        enter-active-class="animate__animated animate__fadeIn animate__faster"
        leave-active-class="animate__animated animate__fadeOut animate__faster">
        <div v-show="$showMoreCustomer">
          <div class="field">
            <div class="is-flex is-justify-content-space-between">
              <label class="label">Emails</label>
              <div>
                <button v-show="activeEmails.length < 2"
                  class="button is-small is-primary is-light tooltip p-0"
                  :class="`is-light-btn-${$theme.name}`"
                  @click="addEmail"
                  data-tooltip="Add Email">
                  <span class="msr msr-24px">add</span>
                </button>
              </div>
            </div>
            <template>
              <div class="field"
                v-for="(email,index) in activeEmails"
                :key="index">
                <div class="field has-addons">
                  <div class="control">
                    <span class="select custom-select">
                      <select v-model="email.type">
                        <option v-for="(emailType,p) in emailTypes"
                          :value="emailType"
                          :key="p"
                          :title="p">
                          {{ Array.from(p)[0] }}
                        </option>
                      </select>
                    </span>
                  </div>
                  <div class="control is-expanded has-icons-right">
                    <input class="input"
                      :class="[ !$v.customer.emails.$each[index].address.email ? 'is-invalid' : '']"
                      type="email"
                      placeholder="Email"
                      v-model="email.address"
                      v-focus-inserted="{ focus: email.isNew }">
                    <span class="icon is-right cursor-pointer"
                      :class="[ email.isDefault ? 'has-text-gold' : 'has-text-grey-light' ]"
                      @click="setDefaultEmail(index)">
                      <i class="mdi mdi-star mdi-24px" />
                    </span>
                  </div>
                  <div class="control">
                    <button class="button has-text-danger"
                      @click="deleteEmail(email, index)">
                      <i class="icon mdi mdi-minus mdi-24px" />
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <div class="field">
            <div class="is-flex is-justify-content-space-between">
              <label class="label">Phones</label>
              <div>
                <button v-show="activePhones.length < 3"
                  class="button is-small is-primary is-light p-0"
                  :class="`is-light-btn-${$theme.name}`"
                  @click="addPhone">
                  <span class="msr msr-24px">add</span>
                </button>
              </div>
            </div>
            <template>
              <div class="field"
                v-for="(phone,index) in activePhones"
                :key="index">
                <div class="field has-addons">
                  <div class="control">
                    <span class="select custom-select">
                      <select v-model="phone.type">
                        <option v-for="(phoneType,p) in phoneTypes"
                          :value="phoneType"
                          :key="p"
                          :title="p">
                          {{ Array.from(p)[0] }}
                        </option>
                      </select>
                    </span>
                  </div>
                  <div class="control is-expanded has-icons-right">
                    <the-mask v-model="phone.no"
                      :mask="phoneNoMask"
                      :tokens="phoneNoTokens"
                      class="input"
                      placeholder="Phone"
                      v-focus-inserted="{ focus: phone.isNew}" />
                    <span class="icon is-right cursor-pointer"
                      :class="[ phone.isDefault ? 'has-text-gold' : 'has-text-grey-light' ]"
                      @click="setDefaultPhone(index)">
                      <i class="mdi mdi-star mdi-24px" />
                    </span>
                  </div>
                  <div class="control">
                    <button class="button has-text-danger"
                      @click="deletePhone(phone, index)">
                      <i class="icon mdi mdi-minus mdi-24px" />
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <div class="field alt-contact-section"> <!-- Wrapper for Alternative Customer Contact -->
            <template v-if="!innerCustomerAltContact">
              <div class="is-flex is-justify-content-space-between is-align-items-center">
                <label class="label mb-0">Alternative Customer Contact</label>
                <button class="button is-small is-primary is-light tooltip p-0"
                  :class="`is-light-btn-${$theme.name}`"
                  @click="addAltContact"
                  data-tooltip="Add Alternative Contact">
                  <span class="msr msr-24px">add</span>
                </button>
              </div>
            </template>
            <template v-else>
              <div class="is-flex is-justify-content-space-between is-align-items-center">
                <label class="label mb-0">Alternative Customer Contact</label>
                <button class="button is-small is-danger is-light tooltip p-0"
                  :class="`is-light-btn-${$theme.name}`"
                  @click="removeAltContact"
                  data-tooltip="Remove Alternative Contact">
                  <span class="msr msr-24px">remove</span>
                </button>
              </div>
              <div class="field-body">
                <div class="field">
                  <label class="label is-required-label">First Name</label>
                  <div class="control">
                    <input v-model="innerCustomerAltContact.contact.firstName"
                      type="text"
                      class="input"
                      :maxlength="120"
                      :class="{ 'is-invalid': $v.customer.contacts.$invalid && !innerCustomerAltContact.contact.firstName }"
                      placeholder="First Name"
                      @input="innerCustomerAltContact.contact.firstName = $event.target.value.trim()">
                  </div>
                </div>
                <div class="field">
                  <label class="label is-required-label">Last Name</label>
                  <div class="control">
                    <input v-model="innerCustomerAltContact.contact.lastName"
                      type="text"
                      class="input"
                      :maxlength="120"
                      :class="{ 'is-invalid': $v.customer.contacts.$invalid && !innerCustomerAltContact.contact.lastName }"
                      placeholder="Last Name"
                      @input="innerCustomerAltContact.contact.lastName = $event.target.value.trim()">
                  </div>
                </div>
              </div>
              <div class="field mt-2"
                v-for="(phone,index) in innerCustomerAltContact.contact.phones"
                :key="index">
                <div class="field has-addons"
                  v-if="phone.isDefault && !phone.isDeleted">
                  <div class="control">
                    <span class="select custom-select">
                      <select v-model="phone.type">
                        <option v-for="(phoneType,p) in phoneTypes"
                          :value="phoneType"
                          :key="p"
                          :title="p">
                          {{ Array.from(p)[0] }}
                        </option>
                      </select>
                    </span>
                  </div>
                  <div class="control is-expanded has-icons-right">
                    <the-mask v-model="phone.no"
                      :mask="phoneNoMask"
                      :tokens="phoneNoTokens"
                      class="input"
                      placeholder="Phone"
                      v-focus-inserted="{ focus: phone.isNew}" />
                    <!-- <span class="icon is-right cursor-pointer"
                      :class="[ phone.isDefault ? 'has-text-gold' : 'has-text-grey-light' ]"
                      @click="setDefaultPhone(index)">
                      <i class="mdi mdi-star mdi-24px" />
                    </span> -->
                  </div>
                </div>
              </div>
            </template>
          </div>
          <customer-address-input ref="addressInput"
            v-model="defaultAddress" />
        </div>
      </transition>
      <!-- <div v-if="$userInfo.isSupportUser || tempGemCan"
        class="field mt-5">
        <div class="is-divider" />
        <label class="label">Enable Assessment History</label>
        <div class="control">
          <div class="pretty p-switch p-fill">
            <input type="checkbox"
              @change="assessmentHistoryChange"
              :disabled="assessmentHistoryToggleDisable"
              :checked="innerQuote.isAssessmentEnabled">
            <div class="state p-primary">
              <label>{{ innerQuote.isAssessmentEnabled ? 'Yes' : 'No' }}</label>
            </div>
          </div>
        </div>
      </div> -->
    </article>
  </div>
</template>

<script>
import _cloneDeep from 'lodash.clonedeep'
import { EmailTypes, PhoneTypes, EventHubTypes } from '@/enums'
import { ormValidationMessages } from '../../QuoteOrmValidation'
import CustomerAddressInput from './CustomerAddressInput.vue'
import { FocusInserted } from '@/components/directives'
import { CustomerService } from '@/services'
import { BulmaAutocomplete } from '@/components/BulmaAutocomplete'
import _debounce from 'lodash.debounce'
import ContactService from '@/views/contact/ContactService'
import DeepDiff from 'deep-diff'
import PanelBlurMixin from './PanelBlurMixin'

export default {
  name: 'QuoteCustomer',
  inject: ['$vv'],
  directives: {
    FocusInserted
  },
  components: {
    CustomerAddressInput,
    BulmaAutocomplete
  },
  mixins: [PanelBlurMixin],
  props: {
    value: null,
    quote: null,
    isNewCustomerPrompt: false,
    isSaving: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      innerCustomer: null,
      innerQuote: null,
      defaultAddress: null,
      phones: [],
      phoneNoTokens: {
        P: {
          pattern: /[0-9, ()\-+]/
        }
      },
      phoneNoMask: Array(31).join('P'), // length of phoneNo field in database
      filteredCustomers: null,
      isSearchingCustomer: false,
      filter: {
        name: '',
        pageIndex: 1,
        pageSize: 50
      },
      maxEmails: 2,
      maxPhones: 3,
      prevInnerQuote: null,
      isWatchPaused: false
    }
  },
  computed: {
    innerCustomerAltContact: function () {
      return this.innerCustomer.contacts.find((c) => c.isDefault && c.isDeleted === false)
    },
    ormMessages() {
      return ormValidationMessages
    },
    $v() {
      return this.$vv || this.$v
    },
    emailTypes: function () {
      return EmailTypes
    },
    phoneTypes() {
      return PhoneTypes
    },
    activePhones: function () {
      if (typeof this.innerCustomer.phones !== 'undefined') {
        return this.innerCustomer.phones.filter((x) => !x.isDeleted).slice(0, this.maxPhones)
      }
      return []
    },
    activeEmails: function () {
      if (typeof this.innerCustomer.emails !== 'undefined') {
        return this.innerCustomer.emails.filter((x) => !x.isDeleted).slice(0, this.maxEmails)
      }
      return []
    },
    tempGemCan() {
      return this.$userInfo.isCustomerAdministrator && this.$company.info.companyId === 'a5ffbdc0-41d2-4e7f-8ecf-e51beac57e6b'
    },
    assessmentHistoryToggleDisable() {
      if (this.innerQuote.quoteAssessments && this.innerQuote.quoteAssessments.length > 0) {
        return true
      }
      if (this.innerQuote.invoices && this.innerQuote.invoices.length > 0) {
        return true
      }
      if (this.innerQuote.orm && this.innerQuote.orm.messageNo != '0') {
        return true
      }
      if (this.innerQuote.subQuoteNos.length > 1) {
        return true
      }
      return false
    }
  },
  watch: {
    innerCustomer: {
      handler: function (newVal, oldVal) {
        if (this.innerCustomer) {
          this.innerCustomer.fullname = this.innerCustomer.isPerson
            ? `${this.innerCustomer.lastname}, ${this.innerCustomer.firstname}`
            : this.innerCustomer.companyName
        }
        this.$emit('input', newVal)
      },
      deep: true
    },
    innerQuote: {
      handler: function (newVal, oldVal) {
        if (this.isWatchPaused) return
        var diff = DeepDiff.diff(this.prevInnerQuote, newVal)
        if (diff) {
          this.prevInnerQuote = _cloneDeep(newVal)
          this.$emit('update:quote', newVal)
        }
      },
      deep: true
    },
    defaultAddress: {
      handler: function (newVal, oldVal) {
        if (
          newVal.isNew &&
          this.innerCustomer.addresses.length === 0 &&
          (newVal.address1 || newVal.suburbText || newVal.suburbPostcode || newVal.countryCode || newVal.countryCode)
        ) {
          this.innerCustomer.addresses.splice(this.innerCustomer.addresses.length, 1, newVal)
        } else if (!newVal.isNew && this.innerCustomer.addresses.length > 0) {
          const defaultAddressIndex = this.innerCustomer.addresses.findIndex((a) => a.isDefault)
          this.innerCustomer.addresses.splice(defaultAddressIndex, 1, newVal)
        }
      },
      deep: true
    }
  },
  created() {
    this.innerCustomer = _cloneDeep(this.value)
    this.innerQuote = _cloneDeep(this.quote)
    if (this.innerQuote && this.innerQuote.isNew) {
      this.$vv.customer.$reset()
    }
    this.getDefaultAddress()
    this.getCustomerDropdownProto()
    // assign alternative contact if there is one
    // this.innerCustomerAltContact = this.innerCustomer.contacts.find((c) => c.isDefault && c.isDeleted === false)
  },
  mounted() {
    console.log('QuoteCustomer - mounted')
    this.$eventHub.$on(`${EventHubTypes.EntitySaved}-${this.$parent.$options.name}`, this.updateInnerCustomer)
    this.$nextTick(() => {
      if (this.innerQuote && !this.innerQuote.isNew && this.innerQuote.orm && this.$vv && this.$vv.customer) {
        if (this.$vv.customer.companyName) this.$vv.customer.companyName.$touch()
        if (this.$vv.customer.firstname) this.$vv.customer.firstname.$touch()
        if (this.$vv.customer.lastname) this.$vv.customer.lastname.$touch()
      }
    })
  },
  beforeDestroy() {
    console.log('QuoteCustomer - beforeDestroy')
    this.$eventHub.$off(`${EventHubTypes.EntitySaved}-${this.$parent.$options.name}`)
  },
  methods: {
    async addAltContact() {
      const assetId = this.innerCustomer.id
      const response = await ContactService.getNewAssetContact('1', assetId)
      let data = response.data
      data.isDefault = true

      data.contact.phones.push({
        id: null,
        isDefault: true,
        isDeleted: false,
        isDirty: false,
        isNew: true,
        mergeOnConflict: false,
        no: '',
        overwriteOnConflict: false,
        type: '0'
      })
      this.innerCustomer.contacts.push(data)
    },
    removeAltContact() {
      if (this.innerCustomerAltContact) {
        if (this.innerCustomerAltContact.isNew) {
          // If it was just added, remove it completely from the array
          const index = this.innerCustomer.contacts.findIndex((c) => c === this.innerCustomerAltContact)
          if (index !== -1) {
            this.innerCustomer.contacts.splice(index, 1)
          }
        } else {
          // If it's an existing contact, mark it as deleted
          this.innerCustomerAltContact.isDeleted = true
        }
      }
    },
    addEmail() {
      if (this.activeEmails.length < 2) {
        const newEmail = {
          id: null,
          type: 0,
          isDefault: this.activeEmails.filter((x) => x.isDefault).length === 0,
          isNew: true,
          isDeleted: false,
          address: ''
        }
        this.innerCustomer.emails.splice(this.innerCustomer.emails.length, 1, newEmail)
      }
    },
    deleteEmail: function (email, index) {
      if (email.isNew) {
        this.innerCustomer.emails.splice(index, 1)
      } else {
        email.isDeleted = true
        email.address = 'deleted@deleted.com'
      }
      if (email.isDefault && this.activeEmails.length > 0) {
        this.activeEmails[0].isDefault = true
      }
    },
    setDefaultEmail(index) {
      this.innerCustomer.emails.forEach((p, i) => {
        p.isDefault = index === i
      })
    },
    addPhone() {
      if (this.activePhones.length < 3) {
        const newPhone = {
          id: null,
          type: this.activePhones.length == 0 ? PhoneTypes.Mobile : PhoneTypes.Home,
          no: '',
          isDefault: this.activePhones.filter((x) => x.isDefault).length === 0,
          isNew: true,
          isDeleted: false
        }
        this.innerCustomer.phones.splice(this.innerCustomer.phones.length, 1, newPhone)
      }
    },
    deletePhone: function (phone, index) {
      if (phone.isNew) {
        this.innerCustomer.phones.splice(index, 1)
      } else {
        phone.isDeleted = true
        phone.no = 'deleted'
      }

      if (phone.isDefault && this.activePhones.length > 0) {
        this.activePhones[0].isDefault = true
        this.activePhones[0].isDirty = true
      }
    },
    setDefaultPhone(index) {
      this.innerCustomer.phones.forEach((p, i) => {
        p.isDefault = index === i
      })
    },
    getDefaultAddress() {
      this.defaultAddress = {
        id: null,
        type: 0,
        addressType: 1,
        address1: '',
        address2: '',
        address3: '',
        suburbText: '',
        suburbPostcode: '',
        stateCode: '',
        stateText: '',
        countryCode: '',
        countryText: '',
        assetID: this.innerCustomer.id,
        isDefault: true,
        isDeleted: false,
        isNew: true
      }
      if (this.innerCustomer.addresses.length > 0 && this.innerCustomer.addresses.some((a) => a.isDefault)) {
        this.defaultAddress = this.innerCustomer.addresses.find((a) => a.isDefault)
      }
    },
    async updateInnerCustomer() {
      console.log(`${this.$options.name} - ${this.value.modifiedDate}`)
      console.log(`${this.$options.name} - ${this.quote.modifiedDate}`)
      await this.$nextTick()
      this.innerCustomer = _cloneDeep(this.value)
      this.innerQuote = _cloneDeep(this.quote)
      this.getDefaultAddress()
      //this.$refs.addressInput.refreshAddress()
    },
    async getCustomerDropdownProto() {
      this.isSearchingCustomer = true
      this.filteredCustomers = await CustomerService.getCustomerDropdownProto(this.filter)
      this.isSearchingCustomer = false
    },
    searchCustomer: _debounce(async function (type) {
      if (type === 'firstname') {
        this.filter.name = this.innerCustomer.firstname
      } else if (type === 'lastname') {
        this.filter.name = this.innerCustomer.lastname
      } else if (type === 'companyName') {
        this.filter.name = this.innerCustomer.companyName
      }
      this.getCustomerDropdownProto()
    }, 300),
    async selectCustomer(selected) {
      if (selected) {
        this.$emit('select-customer', selected.key)
      }
    },
    assessmentHistoryChange(event) {
      this.innerQuote.isAssessmentEnabled = !!event.target.checked
    }
  },
  beforeRouteEnter(to, from, next) {
    next()
  },
  beforeRouteLeave(to, from, next) {
    next()
  }
}
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer !important;

  &.icon {
    pointer-events: auto !important;
  }
}

.is-divider {
  margin: 0 0 1.2rem 0;
}
div.tile.is-parent {
  min-height: calc(100vh - 8rem);
}

.alt-contact-section {
  border-left: 3px solid var(--bulma-primary-70);
  padding-left: 1rem;
  margin-left: 0.25rem;
}

.is-synced-icon {
  position: absolute;
  margin-top: -1em;
  margin-left: -0.5em;
  font-size: 1.5rem;
}
</style>
